<script setup lang="ts">
import { computed } from 'vue';
import { QExpansionItem } from 'quasar';
import { storeToRefs } from 'pinia';

import ConsultationSubscription from '@/components/ConsultationSubscription.vue';
import VideoPlayer from '@/components/VideoPlayer.vue';
import supportingInfoContainer from '@/components/supportingInfoContainer.vue';

import { getLabel, getTranslation } from '@/utils/data-display-utils';
import useResponsive from '@/utils/responsive-util';
import { Section } from '@/store/section.interface';
import { useCommunityStore } from '@/store/communityStore';
import { Context } from '@/store/context.interface';
import { LanguageTranslation, Locales } from '@/store/language.interface';

// STORE
const { isMobile } = useResponsive();
const communityStore = useCommunityStore();
const { sectionLoaded } = storeToRefs(communityStore);

// PROPS
const props = defineProps<{
  context?: string;
  contextAttachment: Array<Context>;
  sections?: Array<Section>;
  isSection?: boolean;
  activeSection?: Section;
  video?: LanguageTranslation | null;
  videoType?: string | null;
  mailingList?: boolean;
  attachmentsOpened: boolean;
  consultationId: number;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'changeSection', newValue: number): void;
  (e: 'openAttachmentDialog'): void;
}>();

// COMPUTED
const videoId = computed(() => (props.video ? getTranslation(props, 'video', props.languageEnabled) : ''));

//FUNCTIONS
function changeSection(id: number) {
  emit('changeSection', id);
  setTimeout(() => {
    const element = document.getElementById('sectionContainer');
    element?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, 200);
}

function openAttachmentDialog() {
  emit('openAttachmentDialog');
}
</script>

<template>
  <div class="context-menu">
    <q-list bordered>
      <q-expansion-item v-bind:default-opened="!isSection && !isMobile">
        <template v-slot:header>
          <q-item-section avatar>
            <q-icon name="o_description"></q-icon>
          </q-item-section>

          <q-item-section v-if="!isSection">
            {{ getLabel('welcome', props.languageEnabled) }}
          </q-item-section>
          <q-item-section v-else>
            {{ getLabel('consultation.context.title', props.languageEnabled) }}
          </q-item-section>
        </template>
        <template v-slot>
          <q-card>
            <q-card-section>
              <div class="context-container__description ck-rich-text" v-html="props.context"></div>
              <div v-if="videoId" class="context-container__description">
                <VideoPlayer
                  :type="props.videoType ?? 'Youtube'"
                  :video-id="props.video!"
                  :language-enabled="props.languageEnabled"
                />
              </div>
            </q-card-section>
          </q-card>
        </template>
      </q-expansion-item>
      <q-expansion-item v-if="props.mailingList">
        <template v-slot:header>
          <q-item-section avatar>
            <q-icon name="o_email"></q-icon>
          </q-item-section>
          <q-item-section>
            {{ getLabel('consultation.mailingList.title', props.languageEnabled) }}
          </q-item-section>
        </template>
        <template v-slot>
          <ConsultationSubscription :consultation-id="props.consultationId" :language-enabled="props.languageEnabled" />
        </template>
      </q-expansion-item>
      <q-expansion-item v-bind:default-opened="props.attachmentsOpened" v-if="props.contextAttachment.length >= 1">
        <template v-slot:header>
          <q-item-section avatar>
            <q-icon name="link"></q-icon>
          </q-item-section>

          <q-item-section>
            <div class="attachments">
              <div>
                {{
                  getLabel('consultation.context.attachment', props.languageEnabled, {
                    named: { count: props.contextAttachment.length },
                  })
                }}
              </div>
              <div class="attachments_count">[ {{ props.contextAttachment.length }} ]</div>
            </div>
          </q-item-section>
        </template>
        <template v-slot>
          <q-card>
            <q-card-section>
              <supporting-info-container
                :supportingInfos="props.contextAttachment"
                :language-enabled="props.languageEnabled"
                @open-attachment-dialog="openAttachmentDialog"
              />
            </q-card-section>
          </q-card>
        </template>
      </q-expansion-item>

      <q-expansion-item
        v-if="!isSection && !isMobile && props.sections.length >= 1 && props.activeSection && sectionLoaded"
      >
        <template v-slot:header>
          <q-item-section avatar>
            <q-icon name="o_view_agenda"></q-icon>
          </q-item-section>

          <q-item-section> Sections </q-item-section>
        </template>
        <template v-slot>
          <q-card>
            <q-card-section v-for="section in sections">
              <button
                class="menu-subsection"
                :class="{ active: section.id === props.activeSection.id }"
                @click="changeSection(section.id)"
                :aria-label="
                  getLabel('aria-label.open.new-section', props.languageEnabled, {
                    named: { title: getTranslation(section, 'title', props.languageEnabled) },
                  })
                "
              >
                <span class="menu-subsection__title">{{
                  getTranslation(section, 'title', props.languageEnabled)
                }}</span>
              </button>
            </q-card-section>
          </q-card>
        </template>
      </q-expansion-item>
    </q-list>
  </div>
</template>

<style lang="scss">
.context-menu {
  font-size: $body-text;
  cursor: pointer;

  .q-item.q-item-type {
    padding: $space-sm $space-md;
    height: auto;
    display: flex;
    align-content: center;
    flex-wrap: wrap;

    .q-icon {
      font-size: $small-title;
      padding: 0 $space-sm;
    }
  }

  .q-card__section {
    font-size: $small-text;

    &.q-card__section--vert {
      padding: 0 $space-md;
    }
  }

  .q-expansion-item:hover {
    background-color: $color-neutral-grey-100;
    font-weight: bold;
    border-radius: 8px;
  }

  .q-expansion-item--expanded .q-item {
    color: $color-primary;

    &.q-item.q-item-type .q-icon {
      color: $color-primary;
    }
  }

  .attachments {
    display: flex;
    flex-direction: row;
    gap: $space-sm;

    &_count {
      display: flex;
      align-items: center;
      font-weight: bold;
      white-space: nowrap;
    }
  }

  .base-media__descriptive-text {
    align-content: end;
    font-size: $small-text;
  }

  .q-expansion-item__content .q-card {
    border-radius: 0px 0px 8px 8px;
  }

  button {
    border: none;
    background-color: transparent;
    text-align: start;
    cursor: pointer;
  }
}

//scrollbar styles
::-webkit-scrollbar {
  border-radius: 5px;
  width: 7px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: $color-neutral-grey-400-50;
  border-radius: 10px;
}

.context-container__description {
  padding: $space-sm;
  font-weight: 400;
  max-height: 45vh;
  overflow-y: auto;

  a {
    font-size: $small-text;
    color: #007bff; /* Primary link color */
    text-decoration: none; /* Removes underline by default */
    border-bottom: 2px solid transparent; /* Subtle bottom border */
    transition:
      color 0.3s,
      border-color 0.3s; /* Smooth transition for color and border */
  }

  a:hover {
    color: #0056b3; /* Darkens on hover */
    border-color: #0056b3;
  }

  a:active {
    outline: none;
    border-color: #004080; /* More prominent color when focused */ /* Slight press effect */
  }
}

.menu-subsection {
  padding: $space-sm;
  padding-left: $space-md;
  font-weight: 400;
  font-size: $small-text;
  margin: 0 $space-sm;

  &__title {
    &:hover {
      color: $color-neutral-grey-400-50;
    }
  }

  &.active {
    color: $color-primary !important;
  }
}

@media screen and (min-width: $breakpoint-md) {
  .context-container__description {
    max-height: 35vh;
  }
}
</style>
